import type { SupabaseClient } from "@supabase/supabase-js";

import { isMissingPolicyRpc } from "./alert-channel-policy";

// PRD-001AE AE3: Group Permissions matrix, console read/write layer.
//
// Host contract (migration 20260812170000, AE2):
//   list_account_role_permission_matrix(p_account_id)
//     -> bucket_key, bucket_label, bucket_order, permission_key, description, role, granted
//   set_account_role_permission(p_account_id, p_role, p_permission_key, p_granted)
//     -> role, permission_key, granted, changed
//
// THE BUCKET MAP LIVES ON THE HOST, NOT HERE. The L3 map is validated inside the list RPC, which
// refuses outright if the live key catalog and the map disagree. So AC-001AE.1, every live key
// exactly once under the locked map, is a server guarantee that this module renders rather than a
// client-side copy that could drift from it. There is deliberately no bucket list in this file.
//
// There is no bucket batch RPC (AE2 chose not to add one). A category toggle is therefore N single
// leaf writes behind one confirm, and it reports partial completion honestly rather than claiming
// the whole bucket moved.

export const LIST_ROLE_PERMISSION_MATRIX_RPC = "list_account_role_permission_matrix";
export const SET_ROLE_PERMISSION_RPC = "set_account_role_permission";

/** Permission that gates every matrix read and write. AE2 reuses member.manage (L5). */
export const MATRIX_MANAGE_PERMISSION = "member.manage";

/**
 * v1 columns (L4). The enum has more values than this UI shows; these are the four the product
 * lock names, in the order it names them. Anything the host returns outside this list is dropped
 * from the grid rather than silently inventing a column.
 */
export const MATRIX_ROLES = ["guard", "supervisor", "admin", "support"] as const;
export type MatrixRole = (typeof MATRIX_ROLES)[number];

/** Officer-facing column labels. "Mobile (guard)" is the L4 mapping, not a new role. */
export const MATRIX_ROLE_LABELS: Readonly<Record<MatrixRole, string>> = {
  guard: "Mobile",
  supervisor: "Supervisor",
  admin: "Admin",
  support: "Support",
};

export const MATRIX_ROLE_HINTS: Readonly<Record<MatrixRole, string>> = {
  guard: "Guard app field work",
  supervisor: "Field leadership and Dispatch console",
  admin: "Full workspace admin",
  support: "SecOps support membership",
};

export interface MatrixLeaf {
  readonly permissionKey: string;
  readonly description: string | null;
  /** Grant state per role. A role missing from the host rows reads as not granted. */
  readonly granted: Readonly<Record<MatrixRole, boolean>>;
}

export interface MatrixBucket {
  readonly bucketKey: string;
  readonly bucketLabel: string;
  readonly bucketOrder: number;
  readonly leaves: readonly MatrixLeaf[];
}

export type RolePermissionMatrixLoad =
  | { readonly status: "ready"; readonly buckets: readonly MatrixBucket[] }
  | { readonly status: "unavailable"; readonly reason: string }
  | { readonly status: "failed"; readonly reason: string };

export const MATRIX_UNAVAILABLE =
  "Group permissions are not available on this server yet. Roles still work exactly as they do today.";

export const MATRIX_LOAD_FAILED =
  "Could not load group permissions. Check your connection and try again.";

function asText(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

function asRole(value: unknown): MatrixRole | null {
  return typeof value === "string" && (MATRIX_ROLES as readonly string[]).includes(value)
    ? (value as MatrixRole)
    : null;
}

function emptyGrants(): Record<MatrixRole, boolean> {
  return { guard: false, supervisor: false, admin: false, support: false };
}

/**
 * Fold the host's one-row-per-(key, role) result into one row per key.
 *
 * Ordering is the host's: bucket_order, then the order keys arrive within a bucket. The client
 * does not re-sort, because the host map is the product lock and re-sorting here would be a second
 * opinion about it.
 */
export function mapRolePermissionMatrix(raw: unknown): MatrixBucket[] {
  if (!Array.isArray(raw)) return [];

  const buckets = new Map<
    string,
    { bucketKey: string; bucketLabel: string; bucketOrder: number; leaves: Map<string, MatrixLeaf> }
  >();

  for (const item of raw) {
    if (item === null || typeof item !== "object") continue;
    const row = item as Record<string, unknown>;
    const bucketKey = asText(row.bucket_key);
    const permissionKey = asText(row.permission_key);
    const role = asRole(row.role);
    if (bucketKey === null || permissionKey === null || role === null) continue;

    const orderValue = Number(row.bucket_order);
    const bucket =
      buckets.get(bucketKey) ??
      {
        bucketKey,
        bucketLabel: asText(row.bucket_label) ?? bucketKey,
        bucketOrder: Number.isFinite(orderValue) ? orderValue : Number.MAX_SAFE_INTEGER,
        leaves: new Map<string, MatrixLeaf>(),
      };
    buckets.set(bucketKey, bucket);

    const existing = bucket.leaves.get(permissionKey);
    const granted = { ...(existing?.granted ?? emptyGrants()) };
    granted[role] = row.granted === true;
    bucket.leaves.set(permissionKey, {
      permissionKey,
      description: asText(row.description) ?? existing?.description ?? null,
      granted,
    });
  }

  return [...buckets.values()]
    .sort((left, right) => left.bucketOrder - right.bucketOrder)
    .map((bucket) => ({
      bucketKey: bucket.bucketKey,
      bucketLabel: bucket.bucketLabel,
      bucketOrder: bucket.bucketOrder,
      leaves: [...bucket.leaves.values()],
    }));
}

/**
 * Never throws. A server without the AE2 migration reads as `unavailable` so the surface can say
 * the feature is not deployed here, rather than showing an empty matrix that would read as
 * "nobody can do anything".
 */
export async function fetchRolePermissionMatrix(
  client: SupabaseClient,
  accountId: string,
): Promise<RolePermissionMatrixLoad> {
  try {
    const { data, error } = await client.rpc(LIST_ROLE_PERMISSION_MATRIX_RPC, {
      p_account_id: accountId,
    });
    if (error === null) {
      return { status: "ready", buckets: mapRolePermissionMatrix(data) };
    }
    if (isMissingPolicyRpc(error)) {
      return { status: "unavailable", reason: MATRIX_UNAVAILABLE };
    }
    const hostMessage = (error as { message?: unknown }).message;
    if (typeof hostMessage === "string" && KNOWN_MATRIX_FAILURES.has(hostMessage)) {
      return { status: "failed", reason: hostMessage };
    }
    return { status: "failed", reason: MATRIX_LOAD_FAILED };
  } catch {
    return { status: "failed", reason: MATRIX_LOAD_FAILED };
  }
}

// --- writes -------------------------------------------------------------------------------------

export const SAFE_MATRIX_WRITE_FAILURE = "Couldn't save that permission change - please try again.";

/**
 * Verbatim refusal sentences from migration 20260812170000. Anything else collapses to the safe
 * sentence, so this surface is never an error oracle about the host's internals.
 */
export const KNOWN_MATRIX_FAILURES: ReadonlySet<string> = new Set([
  "Permission matrix caller mismatch.",
  "Account, role, permission key, and grant state are required.",
  "Member management permission is required.",
  "Account was not found.",
  "Permission key is not recognized.",
  "Admin member.manage cannot be removed because every admin would lose permission matrix access.",
  "The live permission catalog does not match the PRD-001AE bucket map.",
]);

export type MatrixWriteOutcome =
  | { readonly ok: true; readonly changed: boolean }
  | { readonly ok: false; readonly reason: string };

function matrixReason(error: unknown): string {
  const message =
    error !== null && typeof error === "object" && typeof (error as { message?: unknown }).message === "string"
      ? (error as { message: string }).message
      : "";
  return KNOWN_MATRIX_FAILURES.has(message) ? message : SAFE_MATRIX_WRITE_FAILURE;
}

/**
 * One leaf, one call. `changed` echoes the host: a write that matched the effective state is a
 * successful no-op, which matters for the category toggle below so it does not report work it did
 * not do.
 */
export async function setRolePermission(
  client: SupabaseClient,
  accountId: string,
  role: MatrixRole,
  permissionKey: string,
  granted: boolean,
): Promise<MatrixWriteOutcome> {
  try {
    const { data, error } = await client.rpc(SET_ROLE_PERMISSION_RPC, {
      p_account_id: accountId,
      p_role: role,
      p_permission_key: permissionKey,
      p_granted: granted,
    });
    if (error !== null) {
      return { ok: false, reason: matrixReason(error) };
    }
    const row = Array.isArray(data) ? data[0] : data;
    const changed =
      row !== null && typeof row === "object" && (row as { changed?: unknown }).changed === true;
    return { ok: true, changed };
  } catch {
    return { ok: false, reason: SAFE_MATRIX_WRITE_FAILURE };
  }
}

export interface BucketWriteResult {
  readonly attempted: number;
  readonly changed: number;
  /** The first refusal, verbatim when it is one the host publishes. Null when all succeeded. */
  readonly failure: string | null;
  readonly failedKeys: readonly string[];
}

/**
 * A category toggle for one role, as N single leaf writes.
 *
 * AE2 deliberately shipped no bucket batch RPC, so this is not one transaction and must not
 * pretend to be. Writes run in sequence and stop at the first refusal: a bucket containing
 * `member.manage` will refuse partway when un-granting admin, and continuing past that would
 * scatter half-applied state the operator did not ask for. The result reports exactly what landed.
 */
export async function setBucketPermissions(
  client: SupabaseClient,
  accountId: string,
  role: MatrixRole,
  permissionKeys: readonly string[],
  granted: boolean,
): Promise<BucketWriteResult> {
  let changed = 0;
  let attempted = 0;
  for (const permissionKey of permissionKeys) {
    attempted += 1;
    const outcome = await setRolePermission(client, accountId, role, permissionKey, granted);
    if (!outcome.ok) {
      return {
        attempted,
        changed,
        failure: outcome.reason,
        failedKeys: permissionKeys.slice(attempted - 1),
      };
    }
    if (outcome.changed) changed += 1;
  }
  return { attempted, changed, failure: null, failedKeys: [] };
}

// --- presentation -------------------------------------------------------------------------------

export type CategoryState =
  | { readonly kind: "full" }
  | { readonly kind: "partial"; readonly granted: number; readonly total: number }
  | { readonly kind: "empty" };

/** AC-001AE.3: full, partial n/m, or empty for one bucket and one role. */
export function categoryState(bucket: MatrixBucket, role: MatrixRole): CategoryState {
  const total = bucket.leaves.length;
  const granted = bucket.leaves.filter((leaf) => leaf.granted[role]).length;
  if (total === 0 || granted === 0) return { kind: "empty" };
  if (granted === total) return { kind: "full" };
  return { kind: "partial", granted, total };
}

export function categoryStateLabel(state: CategoryState): string {
  if (state.kind === "full") return "All";
  if (state.kind === "empty") return "None";
  return `${state.granted}/${state.total}`;
}

/** Every key the matrix knows, for the count line and the search empty state. */
export function matrixLeafCount(buckets: readonly MatrixBucket[]): number {
  return buckets.reduce((total, bucket) => total + bucket.leaves.length, 0);
}

/**
 * Search over label and key. Buckets with no surviving leaf drop out entirely, so the grid never
 * shows a category header with nothing under it.
 */
export function filterMatrix(buckets: readonly MatrixBucket[], query: string): MatrixBucket[] {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) return [...buckets];
  return buckets
    .map((bucket) => ({
      ...bucket,
      leaves: bucket.leaves.filter(
        (leaf) =>
          leaf.permissionKey.toLowerCase().includes(needle) ||
          (leaf.description ?? "").toLowerCase().includes(needle) ||
          bucket.bucketLabel.toLowerCase().includes(needle),
      ),
    }))
    .filter((bucket) => bucket.leaves.length > 0);
}

/**
 * The one edit this surface refuses before the wire, because the host refuses it too and an
 * operator should learn that from the button rather than from a red banner after the fact.
 */
export function isProtectedLeaf(role: MatrixRole, permissionKey: string, nextGranted: boolean): boolean {
  return role === "admin" && permissionKey === MATRIX_MANAGE_PERMISSION && !nextGranted;
}

export const PROTECTED_LEAF_NOTE =
  "Admin keeps member management. Removing it would lock every admin out of this matrix.";

/** Confirm copy for a category toggle, which is N separate writes and says so. */
export function bucketConfirmMessage(
  bucket: MatrixBucket,
  role: MatrixRole,
  granted: boolean,
): string {
  const verb = granted ? "Grant" : "Remove";
  const count = bucket.leaves.length;
  const noun = count === 1 ? "permission" : "permissions";
  return `${verb} all ${count} ${bucket.bucketLabel} ${noun} for ${MATRIX_ROLE_LABELS[role]}? This is saved one permission at a time, not as a single change.`;
}
